import React, { useState, useEffect } from "react";
import {
  fetchTower,
  fetchFloor,
  fetchSubFloorTower,
} from "../../../domain/usecases/fetchTower";
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";
import { TowerModel } from "../../../domain/entities/TowerModel";
import { FloorTowerModel } from "../../../domain/entities/FloorTowerModel";
import { SubFloorTowerModel } from "../../../domain/entities/SubFloorTowerModel";

interface CascadingApartementDropdownProps {
  onRegionChange : (subFloorId: string) => void;
};

const CascadingApartementDropdown: React.FC<CascadingApartementDropdownProps> = ({onRegionChange}) => {
  const [towers, setTowers] = useState<TowerModel[]>([]);
  const [floors, setFloors] = useState<FloorTowerModel[]>([]);
  const [subFloors, setSubFloors] = useState<SubFloorTowerModel[]>([]);

  const [selectedTower, setSelectedTower] = useState<string>("");
  const [selectedFloor, setSelectedFloor] = useState<string>("");
  const [selectedSubFloor, setSelectedSubFloor] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch Tower on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const towersData = await fetchTower();
        setTowers(towersData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching tower:", err);
        setError("Failed to load tower. Please try again later.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle Tower selection
  const handleTowerChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const towerId = e.target.value;
    setSelectedTower(towerId);

    // If no tower is selected, clear dependent states and exit early
    if (towerId === '') {
      setFloors([]);
      setSelectedFloor("");
      setSubFloors([]);
      return; // Exit early, don't make API call
    }

    try {
      setLoading(true);
      const floorData = await fetchFloor(towerId);
      setFloors(floorData);
      setSelectedFloor("");
      setSubFloors([]);
    } catch (err) {
      console.error("Error fetching Floor:", err);
      setError("Failed to load Floor. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Floor selection
  const handleFloorChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const floorId = e.target.value;
    let floorIdNew = '0';

    if (floorId === '') {
      floorIdNew = "0";
      setSelectedFloor(floorIdNew);
      setSubFloors([]); // Clear sub-floors when no floor is selected
      setSelectedSubFloor("");
      return; // Exit early, don't make API call
    } else {
      floorIdNew = floorId;
    }

    setSelectedFloor(floorIdNew);

    try {
      setLoading(true);
      const subFloorData = await fetchSubFloorTower(selectedTower, floorIdNew);
      setSubFloors(subFloorData);
      setSelectedSubFloor("");
    } catch (err) {
      console.error("Error fetching sub-floor:", err);
      setError("Failed to load sub-floor. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle sub-Floor selection and fetch villages
  const handleSubFloorChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subFloorId = e.target.value;
    setSelectedSubFloor(subFloorId);
    onRegionChange(subFloorId);
  };

  if (loading) {
    return <SpinnerLoading />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Tower Dropdown */}
        <div>
          <select
            value={selectedTower}
            onChange={handleTowerChange}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
          >
            <option value="">Pilih Tower</option>
            {towers.map((towerItem) => (
              <option key={towerItem.id} value={towerItem.name}>
                {towerItem.name}
              </option>
            ))}
          </select>
        </div>

        {/* Floor Dropdown */}
        {floors.length > 0 && (
          <div>
            <select
              value={selectedFloor}
              onChange={handleFloorChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
            >
              <option value="">Pilih Lantai</option>
              {floors.map((floorItem) => (
                <option key={floorItem.id} value={floorItem.id}>
                  {floorItem.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SubFloor Dropdown */}
        {subFloors.length > 0 && (
          <div>
            <select
              value={selectedSubFloor}
              onChange={handleSubFloorChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
            >
              <option value="">Pilih Unit</option>
              {subFloors.map((subFloorItem) => (
                <option key={subFloorItem.id} value={subFloorItem.id}>
                  {subFloorItem.unit}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default CascadingApartementDropdown;
